import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type {
  Besoin,
  SecteurEmployeur,
  StatutRelation,
  TypeContratBesoin,
} from './useEntreprises';
import type { DemandeSuivi, StatutDocument, SuiviDocument } from './useDocuments';
import type { RoleSignataire, StatutSignature } from './useSignatures';
import type { SituationContrat } from './useContrats';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

/**
 * Filtre d'antenne commun aux écrans du pôle : « tous les sites » se traduit par
 * l'absence de filtre, l'API renvoyant alors le périmètre complet du compte.
 */
function parametresSite(site: string): string {
  return site && site !== 'TOUS' ? `?site=${encodeURIComponent(site)}` : '';
}

export type Mobilite = 'PERMIS_B' | 'TRANSPORTS_EN_COMMUN' | 'AUCUNE';
export type SituationRecherche =
  | 'EN_RECHERCHE'
  | 'POST_RUPTURE'
  | 'IMMERSION_PREVUE'
  | 'EN_IMMERSION'
  | 'IMMERSION_A_TRANCHER';
export type CodeTrancheAge = '16-18' | '19-25' | '26+';
export type CodeAnciennete = 'MOINS_1_MOIS' | 'DE_1_A_3_MOIS' | 'PLUS_3_MOIS';
export type EtatRecherche = 'ACTIVE' | 'NON_DECLAREE' | 'TOUTES';
export type FiltreOffres = 'JAMAIS' | 'DEJA';

/**
 * Ligne de « Sans entreprise », rendue par l'API (`domain/sans-contrat.ts`).
 * En vue de placement, les champs personnels ne partent pas : ils sont donc
 * facultatifs ici, et l'écran n'a rien à masquer.
 */
export interface LigneSansContrat {
  apprenantId: string;
  apprenant: string;
  formation: string | null;
  formationCode: string | null;
  siteCode: string;
  rechercheActive: boolean;
  rechercheDepuis: string;
  rechercheDepuisLe: string | null;
  anciennete: CodeAnciennete | null;
  situation: SituationRecherche;
  situationLibelle: string;
  situationTon: string;
  offresRecues: number;
  derniereOffreLe: string | null;
  suggestionsMatching: number;
  meilleurScore: number | null;
  joignable: boolean;
  age?: number | null;
  trancheAge?: CodeTrancheAge | null;
  sexe?: string | null;
  mineur?: boolean;
  rqth?: boolean;
  mobilite?: Mobilite | null;
  mobiliteLibelle?: string | null;
  ville?: string | null;
}

export interface ReponseSansContrat {
  vue: 'COMPLETE' | 'PLACEMENT';
  etat: EtatRecherche;
  apprenants: LigneSansContrat[];
  total: number;
  compteurs: {
    sansEntreprise: number;
    enRechercheActive: number;
    rechercheNonDeclaree: number;
    immersions: number;
    placesCeMois: number;
    rechercheLongue: number;
  };
  filtres: {
    etats: { valeur: EtatRecherche; libelle: string }[];
    tranchesAge: { code: CodeTrancheAge; libelle: string }[];
    mobilites: { valeur: Mobilite; libelle: string }[];
    communes: string[];
    formations: { code: string; titre: string }[];
    situations: { valeur: SituationRecherche; libelle: string }[];
    anciennetes: { code: CodeAnciennete; libelle: string }[];
    offres: { valeur: FiltreOffres; libelle: string }[];
  };
}

export interface FiltresSansContrat {
  site: string;
  etat?: EtatRecherche;
  sexe?: 'F' | 'M';
  trancheAge?: CodeTrancheAge;
  mineur?: boolean;
  rqth?: boolean;
  formation?: string;
  mobilite?: Mobilite;
  commune?: string;
  situation?: SituationRecherche;
  anciennete?: CodeAnciennete;
  offres?: FiltreOffres;
  recherche?: string;
}

export function useSansContrat(filtres: FiltresSansContrat, options: { actif?: boolean } = {}) {
  const parametres = new URLSearchParams({ site: filtres.site });
  for (const cle of [
    'etat',
    'sexe',
    'trancheAge',
    'formation',
    'mobilite',
    'commune',
    'situation',
    'anciennete',
    'offres',
    'recherche',
  ] as const) {
    const valeur = filtres[cle];
    if (valeur) parametres.set(cle, valeur);
  }
  if (filtres.mineur) parametres.set('mineur', 'true');
  if (filtres.rqth) parametres.set('rqth', 'true');

  return useQuery({
    queryKey: ['sans-contrat', parametres.toString()],
    queryFn: () =>
      appelApi<ReponseSansContrat>(`/pedagogique/sans-contrat?${parametres.toString()}`),
    enabled: options.actif ?? true,
  });
}

export function useDefinirMobilite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { apprenantId: string; mobilite: Mobilite }) =>
      appelApi<{ message: string }>(
        `/pedagogique/sans-contrat/${variables.apprenantId}/mobilite`,
        { methode: 'PUT', corps: { mobilite: variables.mobilite } },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sans-contrat'] });
    },
  });
}

export function useDefinirRecherche() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { apprenantId: string; active: boolean }) =>
      appelApi<{ message: string }>(
        `/pedagogique/sans-contrat/${variables.apprenantId}/recherche`,
        { methode: 'PUT', corps: { active: variables.active } },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sans-contrat'] });
    },
  });
}

export interface PersonneEcartee {
  apprenantId: string;
  apprenant: string;
  motif: string;
}

/** Besoin ouvert proposé à la diffusion, avec ses refus : l'écran ne recalcule rien. */
export interface OffreProposee {
  besoinId: string;
  entreprise: string;
  ville: string | null;
  siteCodes: string[];
  formation: string | null;
  formationCode: string | null;
  nombrePostes: number;
  placesRestantes: number;
  dateSouhaitee: string | null;
  profilRecherche: string | null;
  ouvertRqth: boolean;
  proposees: number;
  meilleurScore: number | null;
  memeFormation: number;
  dejaRecue: number;
  aEnvoyer: number;
  refus: string | null;
}

export interface ApercuDiffusion {
  destinataires: number;
  ecartes: PersonneEcartee[];
  offres: OffreProposee[];
  maxOffres: number;
  expedition: EtatExpedition;
}

export function useApercuDiffusion(apprenantIds: string[], actif: boolean) {
  return useQuery({
    queryKey: ['sans-contrat', 'apercu', [...apprenantIds].sort().join(',')],
    queryFn: () =>
      appelApi<ApercuDiffusion>('/pedagogique/sans-contrat/diffusions/apercu', {
        methode: 'POST',
        corps: { apprenantIds },
      }),
    enabled: actif && apprenantIds.length > 0,
  });
}

export interface ResultatDiffusion {
  personnes: number;
  envois: number;
  ecartes: PersonneEcartee[];
  expedition: EtatExpedition;
  message: string;
}

export function useDiffuserOffres() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (corps: { apprenantIds: string[]; besoinIds: string[]; sms: boolean }) =>
      appelApi<ResultatDiffusion>('/pedagogique/sans-contrat/diffusions', {
        methode: 'POST',
        corps,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['sans-contrat'] });
    },
  });
}

export interface ResultatTransmission {
  personnes: number;
  destinataires: number;
  sansDestinataire: string[];
  message: string;
}

export function useTransmettreAuCommercial() {
  return useMutation({
    mutationFn: (corps: { apprenantIds: string[]; message?: string }) =>
      appelApi<ResultatTransmission>('/pedagogique/sans-contrat/transmissions', {
        methode: 'POST',
        corps,
      }),
  });
}

/**
 * Lien d'une ligne avec Top Formation, rendu par l'API (`domain/repertoire.ts`) :
 * l'écran l'affiche, il ne le recalcule pas.
 */
export interface LienRepertoire {
  enLien: boolean;
  libelle: string;
  ton: string;
  details: string[];
}

export interface LigneRepertoire {
  id: string;
  nom: string;
  /** Nature : ENTREPRISE ou un type de partenaire. */
  type: string;
  typeLibelle: string;
  typeTon: string;
  sites: string[];
  /** Partenaire sans antenne déclarée : il vaut pour toutes. */
  toutesAntennes: boolean;
  domaine: string | null;
  email: string | null;
  telephone: string | null;
  contact: string | null;
  adresse: string | null;
  ville: string | null;
  /** SIRET d'une entreprise, RNA d'une association : le même rôle. */
  identifiant: string | null;
  estEntreprise: boolean;
  lien: LienRepertoire;
}

export interface ReponseRepertoire {
  contacts: LigneRepertoire[];
  total: number;
  compteurs: {
    entreprises: number;
    partenaires: number;
    associations: number;
    contacts: number;
  };
  types: { valeur: string; libelle: string }[];
  /** Filtre « Relation » appliqué par l'API, et ses valeurs. */
  relation: string;
  relations: { valeur: string; libelle: string }[];
  statutsRelation: { valeur: StatutRelation; libelle: string; ton: string }[];
  secteursSuggeres: string[];
  typesEmployeurSuggeres: string[];
  employeursSpecifiquesSuggeres: string[];
}

export interface FiltresRepertoire {
  site: string;
  type?: string;
  /** Omis : l'API retient « en lien avec Top Formation ». */
  relation?: string;
  recherche?: string;
}

function parametresRepertoire(filtres: FiltresRepertoire): URLSearchParams {
  const parametres = new URLSearchParams({ site: filtres.site });
  if (filtres.type) parametres.set('type', filtres.type);
  if (filtres.relation) parametres.set('relation', filtres.relation);
  if (filtres.recherche) parametres.set('recherche', filtres.recherche);
  return parametres;
}

export function useRepertoire(filtres: FiltresRepertoire) {
  const parametres = parametresRepertoire(filtres);

  return useQuery({
    queryKey: ['repertoire', parametres.toString()],
    queryFn: () => appelApi<ReponseRepertoire>(`/pedagogique/repertoire?${parametres.toString()}`),
  });
}

/**
 * « Liste générée par site » en fichier (CDC §6.10) : PDF à imprimer, ou CSV
 * pour un tableur. Les filtres sont ceux de la liste affichée : le fichier
 * livre ce qui est à l'écran. Le navigateur porte le cookie de session
 * lui-même, comme pour les autres téléchargements.
 */
export function urlListeRepertoire(filtres: FiltresRepertoire, format: 'pdf' | 'csv'): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/pedagogique/repertoire.${format}?${parametresRepertoire(filtres).toString()}`;
}

/**
 * Fiche d'un partenaire ou d'une association : les mêmes champs que la fiche
 * entreprise, moins ce qui n'a de sens que pour un employeur (SIRET, OPCO,
 * IDCC, effectif). Le RNA y joue le rôle du SIRET.
 */
export interface EntreePartenaire {
  nom: string;
  type: string;
  sites: string[];
  domaine?: string;
  email?: string;
  telephone?: string;
  adresse?: string;
  codePostal?: string;
  ville?: string;
  siteWeb?: string;
  rna?: string;
  contact?: string;
}

/** À la modification, un champ facultatif envoyé vide est effacé côté API. */
export type ModificationPartenaire = Partial<EntreePartenaire>;

function invaliderRepertoire(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['repertoire'] });
}

export function useCreerPartenaire() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: EntreePartenaire) =>
      appelApi<LigneRepertoire>('/pedagogique/repertoire', { methode: 'POST', corps: entree }),
    onSuccess: () => invaliderRepertoire(client),
  });
}

export function useModifierPartenaire() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string } & ModificationPartenaire) => {
      const { id, ...corps } = variables;
      return appelApi<LigneRepertoire>(`/pedagogique/repertoire/${id}`, {
        methode: 'PATCH',
        corps,
      });
    },
    onSuccess: () => invaliderRepertoire(client),
  });
}

// ---------------------------------------------------------------------------
// Documents pédagogiques et suivi documentaire (CDC §6.3 et §6.4)
// ---------------------------------------------------------------------------

export interface SignataireDocument {
  id: string;
  role: RoleSignataire;
  roleLibelle: string;
  nom: string;
  signeLe: string | null;
}

export interface EtatDocumentPedagogique {
  code: string;
  libelle: string;
  portee: string;
  signable: boolean;
  /** Copie du modèle déposé, ou document composé par la plateforme. */
  source: 'MODELE' | 'COMPOSE';
  /** Fichier présenté au signataire, ou attestation générée. */
  documentId: string | null;
  /** Ce qui empêche la mise à la signature, `null` si rien. */
  refusDemande: string | null;
  /** Suivi Vérifié / Validé / Transmis du fichier ; `null` sans fichier. */
  suivi: SuiviDocument | null;
  verifie: boolean;
  valide: boolean;
  transmis: boolean;
  demandeSignatureId: string | null;
  statutSignature: StatutSignature | null;
  signataires: SignataireDocument[];
  signesCount: number;
  total: number;
  empreinteDocument: string | null;
  horodatage: string | null;
}

/** Une version déposée d'un modèle de document pédagogique. */
export interface VersionModelePedagogique {
  id: string;
  nomFichier: string;
  tailleOctets: number;
  deposeLe: string;
  deposePar: string | null;
}

/** Les dépôts d'un modèle : le plus récent fait foi, chacun reste lisible. */
export interface DepotsModele {
  enVigueur: VersionModelePedagogique | null;
  /** Tous les dépôts, le plus récent d'abord. */
  versions: VersionModelePedagogique[];
}

/** Modèle d'un document à signer, commun aux deux antennes. */
export interface ModeleDocumentVue extends DepotsModele {
  code: string;
  libelle: string;
}

/** Fiche REAC d'une formation : sans elle, la fiche se compose depuis le programme. */
export interface FicheReacVue extends DepotsModele {
  formation: { id: string; code: string; titre: string; actif: boolean };
}

export interface ReponseDocumentsPedagogiques {
  modeles: ModeleDocumentVue[];
  /** Une ligne par programme validé. */
  fichesReac: FicheReacVue[];
  /** Ce qui empêche ce compte de déposer un modèle, `null` s'il le peut. */
  refusDepot: string | null;
  apprenants: { id: string; nom: string; siteCode: string; formation: string | null }[];
  apprenant: {
    id: string;
    nom: string;
    siteCode: string;
    formation: string | null;
    formationCode: string | null;
    niveau: string | null;
    rncp: string | null;
    codeDiplome: string | null;
    mineur: boolean;
    modules: { libelle: string; heures: number }[];
    heuresEcrits: number;
    heuresEcf: number;
  } | null;
  documents: EtatDocumentPedagogique[];
  compteurs: { total: number; verifies: number; valides: number; transmis: number };
  legende: Record<string, string>;
  roles: { valeur: RoleSignataire; libelle: string }[];
}

export function useDocumentsPedagogiques(site: string, apprenantId?: string) {
  const parametres = new URLSearchParams();
  if (site && site !== 'TOUS') parametres.set('site', site);
  if (apprenantId) parametres.set('apprenantId', apprenantId);
  const requete = parametres.toString();

  return useQuery({
    queryKey: ['documents-pedagogiques', requete],
    queryFn: () =>
      appelApi<ReponseDocumentsPedagogiques>(
        `/pedagogique/documents${requete ? `?${requete}` : ''}`,
      ),
  });
}

function invaliderDocumentsPedagogiques(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['documents-pedagogiques'] });
  void client.invalidateQueries({ queryKey: ['signatures'] });
  void client.invalidateQueries({ queryKey: ['dashboard-pedagogique'] });
}

/**
 * Dépôt d'un modèle par le pôle pédagogique ou la direction (TF-477) : le
 * modèle commun d'un document, ou la fiche REAC d'une formation.
 */
export function useDeposerModelePedagogique() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { cible: { code: string } | { formationId: string }; fichier: File }) => {
      const formulaire = new FormData();
      formulaire.append('fichier', variables.fichier);
      const chemin =
        'code' in variables.cible
          ? `/pedagogique/documents/modeles/${variables.cible.code}`
          : `/pedagogique/documents/fiches-reac/${variables.cible.formationId}`;
      return appelApi<{ message: string; modele: VersionModelePedagogique }>(chemin, {
        methode: 'POST',
        formulaire,
      });
    },
    onSuccess: () => {
      invaliderDocumentsPedagogiques(client);
      void client.invalidateQueries({ queryKey: ['parametrage'] });
    },
  });
}

/** Mise à la signature : le serveur verse le fichier au dossier et en prend l'empreinte. */
export function useDemanderSignatureDocument() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      code: string;
      apprenantId: string;
      signataires: { role: RoleSignataire; nom: string; email?: string }[];
    }) =>
      appelApi<{ message: string }>(`/pedagogique/documents/${variables.code}/demande`, {
        methode: 'POST',
        corps: { apprenantId: variables.apprenantId, signataires: variables.signataires },
      }),
    onSuccess: () => invaliderDocumentsPedagogiques(client),
  });
}

/** Suivi Vérifié / Validé / Transmis d'un document pédagogique (CDC §6.4). */
export function useAvancerSuiviPedagogique() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: DemandeSuivi) =>
      appelApi<{ message: string; suivi: SuiviDocument }>(
        `/pedagogique/documents/fichiers/${variables.documentId}/statut`,
        { methode: 'PATCH', corps: { statut: variables.statut, destinataire: variables.destinataire } },
      ),
    onSuccess: () => invaliderDocumentsPedagogiques(client),
  });
}

/** Attestation d'entrée en formation, délivrée depuis le suivi documentaire. */
export function useDelivrerAttestationEntree() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (apprenantId: string) =>
      appelApi<{ documentId: string; message: string; avertissements: string[] }>(
        '/pedagogique/documents/attestation-entree',
        { methode: 'POST', corps: { apprenantId } },
      ),
    onSuccess: () => invaliderDocumentsPedagogiques(client),
  });
}

/** Fichier d'un document du suivi documentaire, lu par une route gardée et tracée. */
export function urlDocumentPedagogique(documentId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/pedagogique/documents/fichiers/${documentId}?inline=1`;
}

export function urlModelePedagogique(modeleId: string): string {
  const base = import.meta.env.VITE_API_BASE ?? '/api';
  return `${base}/pedagogique/documents/modeles/${modeleId}/fichier?inline=1`;
}

// ---------------------------------------------------------------------------
// Reporting pédagogique (CDC §6.12)
// ---------------------------------------------------------------------------

export interface LignePromotion {
  cle: string;
  libelle: string;
  siteCode: string;
  formation: string | null;
  effectif: number;
  tauxPresence: number | null;
  demiJourneesEmargees: number;
  absencesInjustifiees: number;
  ruptures: number;
  termines: number;
  /** Blocs acquis en ECF sur blocs attendus ; `null` quand la promotion n'en attend pas. */
  ecfAcquises: number | null;
  ecfAttendues: number | null;
}

export interface ReponseReporting {
  promotions: LignePromotion[];
  compteurs: {
    apprenants: number;
    promotions: number;
    tauxPresence: number | null;
    tauxRupture: number | null;
    tauxReussite: number | null;
  };
  sites: { code: string; apprenants: number; promotions: number; tauxPresence: number | null }[];
}

export function useReporting(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['reporting-pedagogique', parametres],
    queryFn: () => appelApi<ReponseReporting>(`/pedagogique/reporting${parametres}`),
  });
}

// ---------------------------------------------------------------------------
// Hub pédagogique (CDC §6.1)
// ---------------------------------------------------------------------------

export interface ReponseHub {
  compteurs: {
    apprenants: number;
    integrationsImminentes: number;
    preavisIntegrationJours: number;
    absencesDuJour: number;
    seuilsDepasses: number;
    fichesAValider: number;
    attestationsADelivrer: number;
  };
  sites: string[];
}

export function useHubPedagogique(site: string) {
  const parametres = parametresSite(site);
  return useQuery({
    queryKey: ['hub-pedagogique', parametres],
    queryFn: () => appelApi<ReponseHub>(`/pedagogique/hub${parametres}`),
  });
}

// ---------------------------------------------------------------------------
// Dossier d'une organisation du répertoire (entreprise ou partenaire)
// ---------------------------------------------------------------------------

export type GenreOrganisation = 'ENTREPRISE' | 'PARTENAIRE';

export type StatutContrat =
  | 'BROUILLON'
  | 'VALIDE'
  | 'SIGNE'
  | 'TRANSMIS_OPCO'
  | 'EN_COURS'
  | 'ROMPU'
  | 'TERMINE';

export type StatutImmersion = 'PLANIFIEE' | 'EN_COURS' | 'VALIDEE' | 'NON_VALIDEE';

/**
 * Fiche commune à une entreprise et à un partenaire : ce qu'un genre ne porte
 * pas arrive à `null`, et l'écran le masque plutôt que d'afficher un vide.
 */
export interface FicheOrganisation {
  id: string;
  genre: GenreOrganisation;
  nom: string;
  type: string;
  typeLibelle: string;
  typeTon: string;
  sites: string[];
  siret: string | null;
  siren: string | null;
  rna: string | null;
  activite: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  telephone: string | null;
  email: string | null;
  siteWeb: string | null;
  contact: string | null;
  gerant: string | null;
  contactOperationnel: string | null;
  numeroVoie: string | null;
  voie: string | null;
  complementAdresse: string | null;
  secteurEmployeur: SecteurEmployeur | null;
  typeEmployeur: string | null;
  employeurSpecifique: string | null;
  caisseRetraite: string | null;
  codeNaf: string | null;
  opco: string | null;
  idcc: string | null;
  effectif: number | null;
  dernierContactLe: string | null;
  relanceAttendue: boolean;
  actif: boolean;
  creeLe: string;
  modifieLe: string;
}

export interface TuteurDossier {
  id: string;
  nom: string;
  prenom: string;
  emploiOccupe: string | null;
  telephone: string | null;
  email: string | null;
  diplomeLePlusEleve: string | null;
  niveauDiplome: string | null;
  anneesExperience: number | null;
  attestationEligibilite: boolean;
  /** Contrats suivis comme premier maître, tous états confondus. */
  contrats: number;
  /** Apprentis suivis aujourd'hui, calculés par l'API depuis les dates. */
  apprentisSuivis: number;
  /** Plafond légal de deux apprentis simultanés atteint. */
  aPlafond: boolean;
  historique: SuiviMaitre[];
}

/** Un contrat dans l'historique d'un maître d'apprentissage. */
export interface SuiviMaitre {
  contratId: string;
  apprenantId: string;
  apprenant: string;
  formation: string;
  dateDebut: string;
  dateFin: string;
  /** État du contrat, la même lecture que la liste des contrats. */
  situation: SituationContrat;
  /** Le contrat retient le maître aujourd'hui. */
  suiviEnCours: boolean;
  /** Dernier jour où le contrat retient le maître, `AAAA-MM-JJ`. */
  suiviJusquau: string | null;
}

export interface ContratDossier {
  id: string;
  apprenantId: string;
  apprenant: string;
  formation: string;
  formationCode: string;
  type: TypeContratBesoin;
  typeLibelle: string;
  statut: StatutContrat;
  statutLibelle: string;
  statutTon: string;
  dateDebut: string;
  dateFin: string;
  dateSignature: string | null;
  finPeriodeEssai: string | null;
  echeance5Mois: string | null;
  tuteur: string | null;
  rupture: { motif: string | null; dateRupture: string | null; formationMaintenue: boolean } | null;
}

export interface ImmersionDossier {
  id: string;
  apprenantId: string;
  apprenant: string;
  /** Vides pour un positionnement sans immersion. */
  dateDebut: string | null;
  dateFin: string | null;
  sansImmersion: boolean;
  statut: StatutImmersion;
  statutLibelle: string;
  statutTon: string;
  formation: string | null;
  formationCode: string | null;
  avisEntreprise: string | null;
  decideLe: string | null;
  /**
   * Compte rendu d'entretien en entreprise, quand il a été établi. C'est le
   * document du dossier apprenant : le dossier de l'organisation y donne accès,
   * il n'en fabrique pas une copie.
   */
  compteRendu: { id: string; dateVisite: string; modifieLe: string } | null;
}

export interface DocumentDossierOrganisation {
  id: string;
  type: string;
  intitule: string;
  statut: StatutDocument;
  genereLe: string;
  transmisLe: string | null;
  telechargeable: boolean;
  apprenantId: string | null;
  apprenant: string | null;
  /** État de la convention de formation dans le circuit de validation (TF-430). */
  etatConvention?: { texte: string; ton: string } | null;
}

export interface ReferentielsDossier {
  secteursSuggeres: string[];
  typesEmployeurSuggeres: string[];
  employeursSpecifiquesSuggeres: string[];
  statutsRelation: { valeur: StatutRelation; libelle: string; ton: string }[];
  typesPartenaire: { valeur: string; libelle: string; ton: string }[];
  formations: { code: string; titre: string }[];
  typesContrat: { valeur: TypeContratBesoin; libelle: string }[];
}

export interface DossierRepertoire {
  organisation: FicheOrganisation;
  besoins: Besoin[];
  tuteurs: TuteurDossier[];
  contrats: ContratDossier[];
  immersions: ImmersionDossier[];
  documents: DocumentDossierOrganisation[];
  compteurs: {
    postesOuverts: number;
    besoinsOuverts: number;
    besoinsTotal: number;
    contratsEnCours: number;
    contratsTotal: number;
    immersions: number;
    documents: number;
  };
  referentiels: ReferentielsDossier;
}

/**
 * Dossier complet d'une organisation.
 *
 * La clé commence par `repertoire` : la création ou la correction d'une fiche
 * invalide déjà tout ce préfixe. Les mutations d'entreprise, elles, invalident
 * `entreprises` — d'où l'invalidation croisée des deux hooks du dossier.
 */
export function useDossierRepertoire(id: string | undefined) {
  return useQuery({
    queryKey: ['repertoire', 'dossier', id],
    queryFn: () => appelApi<DossierRepertoire>(`/pedagogique/repertoire/${id}`),
    enabled: Boolean(id),
  });
}
